import moment from "moment/moment"
import { getLastDayOfMonth } from "~utils/utils"
import {
  AISuggestionResponse,
  PooCorrectionsPayload,
} from "~shapes/proofOfOwnership"

// Turns the form's per-security values into the exchange-symbol→quantity map the
// corrections endpoint expects (e.g. { "nyse-ccl": 104 }). Keys are already the
// analyzer codes; we just drop empty / non-positive quantities.
export const buildSharesMap = (
  portfolio: Record<string, any> = {},
): Record<string, number> => {
  const shares: Record<string, number> = {}
  Object.keys(portfolio || {}).forEach(code => {
    const qty = parseFloat(portfolio[code])
    if (qty > 0) shares[code] = qty
  })
  return shares
}

// Builds the operator's "final values" payload to compare against the AI
// suggestion (the server computes accepted-vs-edited from it).
export const buildCorrectionsPayload = (
  state: "VALIDATED" | "INVALIDATED",
  formData: {
    name?: string
    broker?: string
    month?: any
    year?: any
    portfolio?: any
    notes?: string
  },
  invalidationReason: string,
  aiSuggestion: AISuggestionResponse,
): PooCorrectionsPayload => {
  // The form only captures month/year, so on save we default the day to the end
  // of the month. But if the operator kept the AI-suggested month/year, send the
  // AI's exact document_date back — otherwise the last-day-of-month default would
  // register as a spurious correction against a date they never changed.
  const aiDate = aiSuggestion.document_date
    ? moment(aiSuggestion.document_date)
    : null
  const keptAiDate =
    aiDate?.isValid() &&
    aiDate.year() === Number(formData.year) &&
    aiDate.month() + 1 === Number(formData.month)
  const document_date =
    aiDate && keptAiDate
      ? aiDate.format("YYYY-MM-DD")
      : moment({
          year: formData.year,
          month: formData.month - 1,
          day: getLastDayOfMonth(formData.month, formData.year),
        }).format("YYYY-MM-DD")

  return {
    state,
    person_name: formData.name || "",
    // Forwarded from the AI suggestion as-is (not surfaced in the UI) so it
    // doesn't read as an edited field in the corrections comparison.
    ...(aiSuggestion.person_names
      ? { person_names: aiSuggestion.person_names }
      : {}),
    brokerage_name: formData.broker || "",
    document_date,
    invalidation_reason: invalidationReason || "NONE",
    shares: buildSharesMap(formData.portfolio),
    ...(formData.notes ? { notes: formData.notes } : {}),
  }
}
